import React, { useState, useContext, useMemo, useEffect } from 'react';
import { useOrder } from '../context/OrderContext';
import { useAllOrders } from '../context/AllOrdersContext';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { AppContext } from '../context/AppContext';
import { OrderItem, CartOrder, PlacedOrder, Dish, Extra, Pack } from '../types';
import DishDetailModal from './DishDetailModal';
import { useGeolocation } from '../hooks/useGeolocation';
import { calculateDistance, calculateDeliveryFee } from '../utils/geo';

const OrderItemRow: React.FC<{ item: OrderItem, onUpdate: (q: number) => void, onEdit: (item: OrderItem) => void }> = ({ item, onUpdate, onEdit }) => {
    const isPack = item.type === 'pack';
    const extrasCost = (item.selectedExtras || []).reduce((sum, extra) => sum + extra.price, 0);
    const unitPrice = isPack ? (item.pack?.packPrice || 0) : (item.dish?.price || 0) + extrasCost;
    const name = isPack ? (item.pack?.name || 'Pack') : (item.dish?.name || 'Plat');
    const image = isPack ? item.pack?.image : item.dish?.image;

    return (
        <div className="bg-white p-4 rounded-2xl border border-gray-100 flex flex-col shadow-sm gap-3">
            <div className="flex items-center">
                <div className="w-16 h-16 rounded-xl mr-4 overflow-hidden bg-gray-50 flex items-center justify-center border border-gray-100 flex-shrink-0">
                    {image ? (
                        <img src={image} alt={name} className="w-full h-full object-cover" />
                    ) : (
                        <span className="text-2xl">{isPack ? '🎁' : '🍽️'}</span>
                    )}
                </div>
                <div className="flex-grow min-w-0">
                    <h3 className="font-bold text-gray-900 truncate text-sm">{name}</h3>
                    <p className="text-sm font-black text-red-600">{unitPrice} DA</p>
                    {isPack && (
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mt-0.5 italic">Pack Promotionnel</p>
                    )}
                </div>

                <div className="flex flex-col items-stretch gap-1.5 w-24">
                    <div className="flex items-center justify-between bg-gray-50 rounded-xl p-0.5 border border-gray-100 h-9">
                        <button onClick={() => onUpdate(item.quantity - 1)} className="flex-1 h-full flex items-center justify-center text-gray-400 hover:text-red-600 font-bold">-</button>
                        <span className="w-6 text-center font-black text-[10px]">{item.quantity}</span>
                        <button onClick={() => onUpdate(item.quantity + 1)} className="flex-1 h-full flex items-center justify-center text-gray-400 hover:text-red-600 font-bold">+</button>
                    </div>
                    {!isPack && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(item);
                            }}
                            className="w-full text-[9px] font-black text-blue-600 uppercase tracking-tighter bg-blue-50 py-1.5 rounded-lg border border-blue-100 hover:bg-blue-100 transition-all active:scale-95 shadow-sm shadow-blue-50"
                        >
                            + Extras
                        </button>
                    )}
                </div>
            </div>

            {item.selectedExtras && item.selectedExtras.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-50">
                    {item.selectedExtras.map(extra => (
                        <div key={extra.id} className="bg-gray-50 px-2 py-0.5 rounded-lg border border-gray-100 flex items-center gap-1.5">
                            <span className="text-[9px] font-bold text-gray-600">{extra.name}</span>
                            <span className="text-[9px] font-black text-red-500">+{extra.price}</span>
                        </div>
                    ))}
                </div>
            )}

            {isPack && item.pack?.dishes && (
                <div className="flex flex-wrap gap-1 pt-2 border-t border-gray-50">
                    <span className="text-[8px] font-black text-gray-400 uppercase mr-1">Contenu:</span>
                    {item.pack.dishes.map((d, idx) => (
                        <span key={idx} className="text-[8px] font-bold text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">
                            {d.quantity && d.quantity > 1 ? `${d.quantity}x ` : ''}{d.name}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

const RestaurantOrderBlock: React.FC<{
    order: CartOrder;
    onConfirm: (items: OrderItem[], total: number, fee: number, type: 'delivery' | 'takeaway', comment?: string) => Promise<void>;
    onClear: () => void;
    updateQuantity: (itemId: string | number, q: number, rId: string) => void;
    onEditItem: (item: OrderItem, rId: string, rName: string) => void;
    isPlacing: boolean;
    userLocation?: { lat: number; lng: number } | null;
    phone: string;
    setPhone: (p: string) => void;
    onGetLocation: () => Promise<void>;
    isGettingLocation?: boolean;
    address: string;
    setAddress: (a: string) => void;
}> = ({ order, onConfirm, onClear, updateQuantity, onEditItem, isPlacing, userLocation, phone, setPhone, onGetLocation, isGettingLocation, address, setAddress }) => {
    const { getRestaurantById } = useData();
    const [deliveryType, setDeliveryType] = useState<'delivery' | 'takeaway'>('delivery');
    const [comment, setComment] = useState("");

    const restaurant = getRestaurantById(order.restaurantId);

    // Force Takeaway if delivery is disabled for this restaurant
    useEffect(() => {
        if (restaurant && restaurant.deliveryEnabled === false && deliveryType === 'delivery') {
            setDeliveryType('takeaway');
        }
    }, [restaurant?.deliveryEnabled, deliveryType]);

    const subtotal = order.items.reduce((sum: number, item: OrderItem) => {
        const extrasCost = (item.selectedExtras || []).reduce((eSum: number, extra: Extra) => eSum + extra.price, 0);
        const unitPrice = item.type === 'pack' ? (item.pack?.packPrice || 0) : (item.dish?.price || 0);
        return sum + (unitPrice + extrasCost) * item.quantity;
    }, 0);

    const deliveryFee = useMemo(() => {
        if (deliveryType === 'takeaway') return 0;
        if (!restaurant?.deliveryEnabled) return 0;

        if (restaurant.location && userLocation) {
            const distance = calculateDistance(
                restaurant.location.lat,
                restaurant.location.lng,
                userLocation.lat,
                userLocation.lng
            );
            return calculateDeliveryFee(distance);
        }

        return restaurant.deliveryPrice || 0;
    }, [deliveryType, restaurant, userLocation]);

    const distanceText = useMemo(() => {
        if (deliveryType === 'takeaway' || !restaurant?.location || !userLocation) return null;
        const distance = calculateDistance(
            restaurant.location.lat,
            restaurant.location.lng,
            userLocation.lat,
            userLocation.lng
        );
        return distance.toFixed(1) + " km";
    }, [deliveryType, restaurant, userLocation]);

    const finalTotal = subtotal + deliveryFee;

    return (
        <div className="bg-white rounded-[2.5rem] shadow-xl shadow-gray-100 border border-gray-50 overflow-hidden mb-8 animate-fade-in-up">
            <div className="bg-gray-50/50 px-6 py-4 flex justify-between items-center border-b border-gray-100">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-red-100">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                    </div>
                    <div>
                        <h2 className="font-black text-gray-900 leading-tight">{order.restaurantName}</h2>
                        <p className="text-[10px] uppercase font-black tracking-widest text-gray-400">Commande en attente</p>
                    </div>
                </div>
                <button onClick={onClear} className="text-gray-300 hover:text-red-600 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </button>
            </div>

            {restaurant && restaurant.globalDiscount > 0 && (
                <div className="bg-red-50 border-b border-red-100 px-6 py-2 flex items-center gap-2">
                    <span className="text-[10px] font-black text-red-600 uppercase tracking-widest italic animate-pulse">🎉 Offre de l'établissement: -{restaurant.globalDiscount}% sur tous les plats</span>
                </div>
            )}

            <div className="p-6 space-y-4">
                {order.items.map(item => (
                    <OrderItemRow
                        key={item.type === 'pack' ? item.pack?.id : item.dish?.id}
                        item={item}
                        onUpdate={(q) => updateQuantity(item.type === 'pack' ? item.pack!.id : item.dish!.id, q, order.restaurantId)}
                        onEdit={(it) => it.type === 'dish' && onEditItem(it, order.restaurantId, order.restaurantName)}
                    />
                ))}

                <div className="flex gap-2 p-1 bg-gray-100 rounded-2xl mt-6 relative">
                    <button
                        onClick={() => restaurant?.deliveryEnabled !== false && setDeliveryType('delivery')}
                        className={`flex-1 py-3 rounded-[1.25rem] text-xs font-black uppercase tracking-widest transition-all ${deliveryType === 'delivery' ? 'bg-white text-red-600 shadow-sm' : 'text-gray-400'} ${restaurant?.deliveryEnabled === false ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                        Livraison
                    </button>
                    <button
                        onClick={() => setDeliveryType('takeaway')}
                        className={`flex-1 py-3 rounded-[1.25rem] text-xs font-black uppercase tracking-widest transition-all ${deliveryType === 'takeaway' ? 'bg-white text-red-600 shadow-sm' : 'text-gray-400'}`}
                    >
                        À emporter
                    </button>
                </div>

                {restaurant?.deliveryEnabled === false && (
                    <div className="bg-orange-50 p-3 rounded-xl border border-orange-100 flex items-center gap-2 mt-2">
                        <span className="text-lg">ℹ️</span>
                        <p className="text-[10px] font-bold text-orange-700 uppercase tracking-tight">Le restaurant ne propose que la vente à emporter pour le moment.</p>
                    </div>
                )}

                <div className="mt-6 pt-6 border-t border-dashed border-gray-100 space-y-2">
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-400 font-bold">Sous-total</span>
                        <span className="text-gray-900 font-black">{subtotal} DA</span>
                    </div>

                    {deliveryType === 'delivery' && (
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-400 font-bold">Livraison</span>
                            {userLocation ? (
                                <span className={deliveryFee === 0 ? 'text-green-500 font-black' : 'text-gray-900 font-black'}>
                                    {deliveryFee === 0 ? 'GRATUIT' : `${deliveryFee} DA`}
                                </span>
                            ) : (
                                <span className="text-amber-500 text-[10px] font-black uppercase tracking-tight">Position requise</span>
                            )}
                        </div>
                    )}

                    <div className="flex justify-between text-lg pt-2 mt-2 border-t border-gray-50">
                        <span className="text-gray-900 font-black">Total</span>
                        <span className="text-red-600 font-black">{userLocation || deliveryType === 'takeaway' ? `${finalTotal} DA` : `${subtotal} DA + ?`}</span>
                    </div>
                </div>

                {/* Note pour le restaurant */}
                <div className="mt-4 p-5 bg-gray-50 rounded-[1.5rem] border border-gray-100 relative overflow-hidden">
                    <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">Note pour le restaurant</h3>
                    <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value.slice(0, 163))}
                        maxLength={163}
                        rows={3}
                        placeholder="Ex: Pas de tomates, s'il vous plaît..."
                        className="w-full p-3 bg-white border border-gray-100 rounded-xl text-gray-900 font-bold text-xs focus:outline-none focus:ring-2 focus:ring-red-600 transition-all resize-none"
                    />
                    <div className="text-right mt-1">
                        <span className={`text-[9px] font-black ${comment.length >= 163 ? 'text-red-500' : 'text-gray-300'}`}>
                            {comment.length}/163
                        </span>
                    </div>
                </div>

                {/* Contact Livraison moved here */}
                <div className="mt-4 p-5 bg-gray-900 rounded-[1.5rem] shadow-lg relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -mr-12 -mt-12" />
                    <h3 className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] mb-3">Contact Livraison</h3>
                    <input
                        type="tel"
                        value={phone}
                        onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setPhone(val);
                        }}
                        maxLength={10}
                        placeholder="Votre numéro de mobile"
                        className="w-full p-3 bg-white/10 border border-white/10 rounded-xl text-white font-black text-base focus:outline-none focus:ring-2 focus:ring-red-600 transition-all placeholder:text-white/20"
                    />
                </div>

                {/* Adresse de livraison - CRITICAL for accuracy */}
                {deliveryType === 'delivery' && (
                    <div className="mt-4 p-5 bg-white border-2 border-red-100 rounded-[1.5rem] shadow-sm relative overflow-hidden">
                        <div className="flex justify-between items-center mb-3">
                            <h2 className="text-[10px] font-black text-red-600 uppercase tracking-[0.2em]">Complément d'adresse</h2>
                            <button 
                                onClick={onGetLocation}
                                disabled={isGettingLocation}
                                className="text-[9px] font-black text-blue-600 bg-blue-50 px-2 py-1 rounded-lg uppercase tracking-wider flex items-center gap-1 active:scale-95 transition-all"
                            >
                                📍 {isGettingLocation ? 'Lecture...' : 'Actualiser GPS'}
                            </button>
                        </div>
                        <textarea
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="Etage, Appt, N° de villa, Point de repère... (Optionnel)"
                            rows={2}
                            className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl text-gray-900 font-bold text-xs focus:outline-none focus:ring-2 focus:ring-red-600 transition-all resize-none placeholder:text-gray-300"
                        />
                        <p className="text-[8px] text-gray-400 mt-2 font-medium italic">* Optionnel, mais aide le livreur à vous trouver plus vite.</p>
                    </div>
                )}

                {deliveryType === 'delivery' && !userLocation ? (
                    <div className="mt-6 p-4 bg-amber-50 rounded-2xl border border-amber-100 text-center">
                        <p className="text-xs text-amber-700 font-bold mb-3">Veuillez définir votre position pour calculer les frais et commander.</p>
                        <button
                            onClick={onGetLocation}
                            disabled={isPlacing || isGettingLocation}
                            className={`w-full py-3 text-white font-black rounded-xl text-[10px] uppercase tracking-widest shadow-lg transition-all outline-none ${isGettingLocation ? 'bg-amber-400' : 'bg-amber-500 active:scale-95 shadow-amber-100'}`}
                        >
                            {isGettingLocation ? 'RECHERCHE GPS...' : 'Partager ma position'}
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={() => {
                            onConfirm(order.items, finalTotal, deliveryFee, deliveryType, comment.trim());
                        }}
                        disabled={isPlacing}
                        className="w-full mt-6 py-4 bg-red-600 text-white font-black rounded-2xl shadow-xl shadow-red-100 active:scale-95 transition-all text-sm uppercase tracking-widest disabled:opacity-50"
                    >
                        {isPlacing ? 'CONFIRMATION...' : 'CONFIRMER L\'ACHAT'}
                    </button>
                )}
            </div>
        </div>
    );
};

const CartOverlay: React.FC = () => {
    const { isCartOpen, setIsCartOpen, setView, view, navigateAndClearCart } = useContext(AppContext);
    const { cartOrders, updateQuantity, clearOrder } = useOrder();
    const { placeOrder } = useAllOrders();
    const { currentUser, updateUserProfile } = useAuth();
    const { getRestaurantById, restaurants } = useData();

    const [confirmedOrder, setConfirmedOrder] = useState<PlacedOrder | null>(null);
    const [isPlacingOrder, setIsPlacingOrder] = useState(false);
    const [phone, setPhone] = useState(currentUser?.phone || localStorage.getItem('yumii_customer_phone') || '');
    const [address, setAddress] = useState((currentUser as any)?.address || localStorage.getItem('yumii_customer_address') || '');

    useEffect(() => { if (currentUser?.phone) setPhone(currentUser.phone); }, [currentUser]);
    useEffect(() => { if ((currentUser as any)?.address) setAddress((currentUser as any).address); }, [(currentUser as any)?.address]);
    useEffect(() => { if (phone) localStorage.setItem('yumii_customer_phone', phone); }, [phone]);
    useEffect(() => { 
        if (address) {
            localStorage.setItem('yumii_customer_address', address);
            // If user manually clears address, we should perhaps warn, but for now just sync
        }
    }, [address]);

    const { location, getCurrentPosition, error: geoError, errorCode } = useGeolocation(currentUser?.uid);
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const [editingData, setEditingData] = useState<{ item: OrderItem, rId: string, rName: string } | null>(null);
    const editingDishId = view.params?.editingDishId;

    // Sync editing state with URL/History
    useEffect(() => {
        if (editingDishId) {
            // Find item in cart
            const allItems = cartOrders.flatMap(o => o.items);
            const item = allItems.find(i => {
                const id = i.type === 'pack' ? i.pack?.id : i.dish?.id;
                return String(id) === String(editingDishId);
            });
            if (item) {
                const currentItemId = item.type === 'pack' ? item.pack?.id : item.dish?.id;
                const order = cartOrders.find(o => o.items.some(k => (k.type === 'pack' ? k.pack?.id : k.dish?.id) === currentItemId));
                if (order) {
                    setEditingData({ item, rId: order.restaurantId, rName: order.restaurantName });
                }
            }
        } else {
            setEditingData(null);
        }
    }, [editingDishId, cartOrders]);

    if (!isCartOpen) return null;

    const handleConfirmOrder = async (items: OrderItem[], total: number, fee: number, type: 'delivery' | 'takeaway', comment?: string) => {
        if (!currentUser) { setView({ screen: 'account' }); setIsCartOpen(false); return; }

        const restaurantId = items[0].restaurantId;
        const restaurant = getRestaurantById(restaurantId);

        // EXTRA SECURITY: Block delivery if restaurant disabled it
        let finalType = type;
        let finalFee = fee;
        let finalTotal = total;

        if (restaurant && restaurant.deliveryEnabled === false && type === 'delivery') {
            alert("Ce restaurant ne propose plus la livraison. Votre commande a été basculée en 'À emporter'.");
            finalType = 'takeaway';
            finalFee = 0;
            finalTotal = total - fee;
        }

        const cleanedPhone = phone.trim();
        if (cleanedPhone.length < 9) { alert("Numéro de téléphone invalide."); return; }

        if (finalType === 'delivery' && !currentUser.location && !location) {
            const confirmed = window.confirm("La localisation est requise pour la livraison. Voulez-vous que Yumii récupère votre position actuelle ?");
            if (confirmed) {
                setIsGettingLocation(true);
                const loc = await getCurrentPosition();
                setIsGettingLocation(false);
                if (!loc) {
                    if (errorCode === 1) {
                        alert("La localisation est bloquée. Veuillez l'activer dans les paramètres de Chrome (Paramètres > Paramètres de site > Localisation) puis réessayez.");
                    } else {
                        alert("Impossible de récupérer votre position. Vérifiez que le GPS est activé et que Yumii est autorisé à y accéder.");
                    }
                    return;
                }
            } else {
                return;
            }
        }

        if (currentUser.phone !== cleanedPhone && updateUserProfile) {
            updateUserProfile({ phone: cleanedPhone }).catch(console.error);
        }

        setIsPlacingOrder(true);
        try {
            // Try to get freshest location possible
            let finalLocation = location || currentUser.location;
            
            // If we are in delivery mode and haven't gotten a fresh location yet, try one last time
            if (finalType === 'delivery' && !location) {
                console.log("[Cart] No fresh location yet, attempting last-minute fetch...");
                const freshLoc = await getCurrentPosition();
                if (freshLoc) {
                    finalLocation = freshLoc;
                }
            }

            const newOrder = await placeOrder(items, finalTotal, finalFee, cleanedPhone, finalType, finalLocation, comment, address.trim());
            if (newOrder) {
                setConfirmedOrder(newOrder);
                clearOrder(items[0].restaurantId);
            } else {
                throw new Error("Erreur serveur lors de l'envoi.");
            }
        } catch (error: any) {
            alert("Erreur: " + error.message);
        } finally {
            setIsPlacingOrder(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[1001] flex flex-col bg-gray-50 animate-fade-in overflow-hidden pt-safe-top">
            {/* Header */}
            <div className="px-6 py-4 flex justify-between items-center bg-white border-b border-gray-100 shadow-sm">
                <h1 className="text-2xl font-black text-gray-900 tracking-tight">Mon Panier</h1>
                <button
                    onClick={() => {
                        setIsCartOpen(false);
                        setConfirmedOrder(null);
                    }}
                    className="p-2 bg-gray-100 text-gray-400 rounded-full hover:bg-gray-200 hover:text-gray-600 transition-all active:scale-95"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-8 pb-10">
                {confirmedOrder ? (
                    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
                        <div className="w-24 h-24 bg-green-50 rounded-[2.5rem] flex items-center justify-center mb-8 animate-bounce shadow-xl shadow-green-50">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h1 className="text-4xl font-black text-gray-900 mb-4 tracking-tight">C'est parti ! 🚀</h1>
                        <p className="text-gray-500 font-bold mb-10 max-w-xs mx-auto text-sm leading-relaxed">
                            Votre commande a été transmise au restaurant.
                        </p>
                        <button
                            onClick={() => { 
                                setConfirmedOrder(null); 
                                navigateAndClearCart({ screen: 'orders' });
                            }}
                            className="w-full max-w-xs py-4 bg-gray-900 text-white font-black rounded-2xl shadow-xl active:scale-95 transition-all text-sm uppercase tracking-[0.2em]"
                        >
                            Continuer
                        </button>
                    </div>
                ) : cartOrders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
                        <div className="w-24 h-24 bg-red-50 rounded-[2.5rem] flex items-center justify-center mb-8 shadow-xl shadow-red-50">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                        </div>
                        <h1 className="text-3xl font-black text-gray-900 mb-3 tracking-tight">Panier vide</h1>
                        <p className="text-gray-400 font-bold mb-10 max-w-xs mx-auto text-sm">Votre estomac vous attend ! Choisissez vite de quoi vous régaler.</p>
                        <button
                            onClick={() => { setIsCartOpen(false); setView({ screen: 'restaurants' }); }}
                            className="w-full max-w-xs py-4 bg-red-600 text-white font-black rounded-2xl shadow-xl shadow-red-100 active:scale-95 transition-all text-sm uppercase tracking-widest"
                        >
                            Explorer les cartes
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="flex justify-between items-end mb-10">
                            <div>
                                <h2 className="text-3xl font-black text-gray-900 tracking-tight">Panier</h2>
                                <div className="flex items-center gap-2 mt-1">
                                    <div className="w-2 h-2 bg-red-600 rounded-full animate-pulse" />
                                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                                        {cartOrders.length} restaurant{cartOrders.length > 1 ? 's' : ''} en attente
                                    </p>
                                </div>
                            </div>
                            <button onClick={() => clearOrder()} className="text-xs font-black text-red-600 uppercase tracking-widest bg-red-50 px-4 py-2 rounded-full">Vider</button>
                        </div>

                        <div className="space-y-4">
                            {cartOrders.map(order => (
                                <RestaurantOrderBlock
                                    key={order.restaurantId}
                                    order={order}
                                    onConfirm={handleConfirmOrder}
                                    onClear={() => clearOrder(order.restaurantId)}
                                    updateQuantity={updateQuantity}
                                    onEditItem={(item, rId, rName) => setView({ ...view, params: { ...view.params, editingDishId: (item.type === 'pack' ? item.pack?.id : item.dish?.id) } })}
                                    isPlacing={isPlacingOrder}
                                    userLocation={location || currentUser?.location}
                                    phone={phone}
                                    setPhone={setPhone}
                                    address={address}
                                    setAddress={setAddress}
                                    onGetLocation={async () => {
                                        setIsGettingLocation(true);
                                        try {
                                            const loc = await getCurrentPosition();
                                            if (!loc) {
                                                if (errorCode === 1) {
                                                    alert("La localisation est bloquée. Veuillez l'activer dans les paramètres de Chrome (Paramètres > Paramètres de site > Localisation) puis réessayez.");
                                                } else {
                                                    alert("Impossible de récupérer la position. Vérifiez que le GPS est activé et que Yumii est autorisé à y accéder.");
                                                }
                                            }
                                        } catch (err: any) {
                                            alert("Erreur GPS : " + (err.message || "inconnue"));
                                        } finally {
                                            setIsGettingLocation(false);
                                        }
                                    }}
                                    isGettingLocation={isGettingLocation}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>

            {editingData && (
                <DishDetailModal
                    dish={editingData.item.type === 'dish' ? (editingData.item.dish || {} as Dish) : ({} as Dish)}
                    restaurantId={editingData.rId}
                    restaurantName={editingData.rName}
                    onClose={() => {
                        if (view.params?.editingDishId) {
                            window.history.back();
                        } else {
                            setEditingData(null);
                        }
                    }}
                    minimal={true}
                />
            )}
        </div>
    );
};

export default CartOverlay;
